import type { IAiSpreadsheetCell } from "@/payroll/ai-script/types";

export const calculatePracticeCells = (cells: IAiSpreadsheetCell[]) => {
  const byAddress = new Map(
    cells.map((cell) => [
      `${String.fromCharCode(64 + cell.col)}${cell.row}`,
      cell,
    ]),
  );
  const evaluateCell = (
    cell: IAiSpreadsheetCell,
    visiting = new Set<string>(),
  ): string | number => {
    const raw = cell.raw_input?.trim() ?? "";
    if (cell.kind !== "formula") {
      return raw !== "" && Number.isFinite(Number(raw)) ? Number(raw) : raw;
    }
    if (visiting.has(cell.id)) throw new Error("Circular reference");
    const next = new Set(visiting).add(cell.id);
    const reference = (address: string) => {
      const target = byAddress.get(address);
      if (!target) throw new Error("Cell reference is outside the sample");
      const value = evaluateCell(target, next);
      if (typeof value === "string" && value !== "")
        throw new Error("A formula refers to text");
      return Number(value);
    };
    const expression = raw
      .slice(1)
      .toUpperCase()
      .replace(/\$/g, "")
      .replace(/\s/g, "")
      .replace(/SUM\(([A-D])([1-5]):([A-D])([1-5])\)/g, (_, a, r, b, s) => {
        let total = 0;
        for (let row = Math.min(+r, +s); row <= Math.max(+r, +s); row++) {
          for (
            let col = Math.min(a.charCodeAt(0), b.charCodeAt(0));
            col <= Math.max(a.charCodeAt(0), b.charCodeAt(0));
            col++
          ) {
            const target = byAddress.get(`${String.fromCharCode(col)}${row}`);
            if (!target) continue;
            const value = evaluateCell(target, next);
            if (typeof value === "number") total += value;
          }
        }
        return String(total);
      })
      .replace(/[A-Z]+\d+/g, (address) => `(${reference(address)})`);
    let position = 0;
    const factor = (): number => {
      const sign = expression[position];
      if (sign === "+" || sign === "-") {
        position++;
        return (sign === "-" ? -1 : 1) * factor();
      }
      if (expression[position] === "(") {
        position++;
        const value = sum();
        if (expression[position++] !== ")")
          throw new Error("Check the formula");
        return value;
      }
      const number = expression.slice(position).match(/^(?:\d+\.?\d*|\.\d+)/);
      if (!number) throw new Error("Use arithmetic or SUM in this sample");
      position += number[0].length;
      return Number(number[0]);
    };
    const product = (): number => {
      let value = factor();
      while (expression[position] === "*" || expression[position] === "/") {
        const operator = expression[position++];
        const right = factor();
        value = operator === "*" ? value * right : value / right;
      }
      return value;
    };
    const sum = (): number => {
      let value = product();
      while (expression[position] === "+" || expression[position] === "-") {
        const operator = expression[position++];
        const right = product();
        value = operator === "+" ? value + right : value - right;
      }
      return value;
    };
    const result = sum();
    if (position !== expression.length || !Number.isFinite(result))
      throw new Error("Check the formula");
    return result;
  };
  return cells.map((cell) => {
    try {
      return { ...cell, computed_value: evaluateCell(cell), error: null };
    } catch (error) {
      return {
        ...cell,
        computed_value: null,
        error: error instanceof Error ? error.message : "Check the formula",
      };
    }
  });
};
